import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../lib/api';
import { H2, H3, Muted } from '../components/text';
import { MatchCard } from '../components/match-card';
import { ISponsor } from '../models/sponsor';
import { IPage } from '../models/page';
import { IMenuItem } from '../models/menu-item';
import { IMatchRoundData } from '../models/matchRoundData';
import { getDateFormat } from '../utils/date';
import { useRoundsAndCalendars } from '@/utils/useRoundsAndCalendars';

interface IProps {
	matchRoundsData: IMatchRoundData;
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	pageData: IPage;
	calendars: Option[];
}

type Option = {
	name: string;
	id: string;
	rounds?: number;
	periods?: number[];
};

export default function Uitslagen({
	primaryMenuItems,
	pageData: { page },
	sponsors,
	matchRoundsData,
	calendars,
}: IProps) {
	const { roundData, currentRound, setCurrentRound, currentYear, setCurrentYear, roundsCount, setRoundsCount } =
		useRoundsAndCalendars(matchRoundsData, calendars);

	// on initial load set the currentround to currentround -1 to alway show the previous round except for the last round
	useEffect(() => {
		// Only set to previous round if not already at the last round
		if (currentRound && currentRound > 1 && currentRound < roundsCount) {
			setCurrentRound(currentRound - 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getRoundsOptions = () => {
		let rounds = [];
		const totalRounds = currentYear?.id === calendars[0].id ? matchRoundsData?.currentRound : roundsCount;

		for (let i = 1; i <= totalRounds; i++) {
			rounds.push(<option key={i} value={i}>{`Speelronde ${i}`}</option>);
		}

		return rounds;
	};

	const handleSeasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedSeason = calendars.find((season) => season.id === event.target.value);
		if (selectedSeason) {
			setCurrentYear({
				id: selectedSeason.id,
				name: selectedSeason.name,
				rounds: selectedSeason.rounds,
			});
			setRoundsCount(selectedSeason.rounds || 0); // Update the rounds count state with a default value of 0
			setCurrentRound(1); // Reset the current round to 1 on season change
		}
	};

	const handleRoundChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentRound(parseInt(event.target.value));
	};

	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />

			{roundData && (
				<Container className="py-12 xl:py-16">
					<article>
						<H2 className="text-center py-6">{page.title}</H2>

						<div className="pb-5 sm:flex sm:items-end sm:justify-between gap-4">
							<div className="flex-1">
								<div className="flex sm:flex-col ">
									<div className="flex flex-col">
										<H3 className="mb-1">Speelronde {roundData.currentRound}</H3>
										{roundData.start_date && roundData.end_date && (
											<Muted className="font-bold">
												{getDateFormat(roundData.start_date)} -{' '}
												{getDateFormat(roundData.end_date)}
											</Muted>
										)}
									</div>
								</div>
							</div>
							<div className="flex-1 mt-4">
								<div className="block sm:flex justify-center sm:justify-end gap-4">
									<div className="flex-1">
										<label htmlFor="season" className="block text-base">
											Seizoen
										</label>
										<select
											id="season"
											value={currentYear?.id}
											onChange={handleSeasonChange}
											className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10  ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
										>
											{calendars.map((option: Option) => (
												<option key={option.id} value={option.id}>
													{option.name}
												</option>
											))}
										</select>
									</div>
									<div className="flex-1">
										<label htmlFor="Round" className="block text-base">
											Speelronde
										</label>
										<select
											id="Round"
											value={currentRound}
											onChange={handleRoundChange}
											className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10  ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
										>
											{getRoundsOptions()}
										</select>
									</div>
								</div>
							</div>
						</div>
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
							{roundData?.matches?.map((match, index) => (
								<Link href={`/wedstrijden/${match.slug}`} key={index}>
									<MatchCard
										venue={match.venue}
										date={match.date}
										time={match.time}
										homeContestant={match.homeContestant}
										awayContestant={match.awayContestant}
										status={match.status}
										scores={match.scores}
										channel={match.channel}
										snug
										className="cursor-pointer hover:shadow-lg transition-shadow duration-300"
									/>
								</Link>
							))}
						</div>
					</article>
				</Container>
			)}
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(
		`
		query {
			page(id: 115015, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	let matchRoundsData: IMatchRoundData | null = null;
	const fetchStandingsData = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/current-round`;
		return fetchData(endpoint);
	};

	try {
		matchRoundsData = await fetchStandingsData();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	let calendars: Option[] | null = null;
	const fetchCalenders = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/available-calendars`;
		return fetchData(endpoint);
	};

	try {
		calendars = await fetchCalenders();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	return {
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			matchRoundsData,
			calendars,
		},
		revalidate: 300,
	};
};
